import React, { useState } from 'react';
import {
    Modal, Form, Input, Select, Button, Space, Card, Typography, DatePicker,
    Divider, Tag, Row, Col, Empty, message,
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, RobotOutlined, ApartmentOutlined,
} from '@ant-design/icons';
import type { MissionItem, DeviceItem } from './missionModels';
import { TRIGGER_TYPE_OPTIONS } from './operationModels';
import type { OperationMissionTrigger } from './operationModels';

const { Text } = Typography;
const { TextArea } = Input;

interface MissionStepDraft {
    key: string;
    missionId: number | null;
    deviceId: number | null;
    executionOrder: number;
    triggerType: OperationMissionTrigger;
    dependsOnKeys: string[];
}

interface OperationBuilderProps {
    open: boolean;
    onClose: () => void;
    onSave: (payload: {
        name: string;
        description?: string;
        operationType?: string;
        config?: Record<string, any> | null;
        scheduledStartAt?: string | null;
        missions: Array<{
            missionId: number;
            deviceId?: number;
            executionOrder: number;
            triggerType?: string;
            triggerCondition?: string;
            dependsOnOperationMissionIds?: number[];
        }>;
    }) => Promise<void>;
    missions: MissionItem[];
    devices: DeviceItem[];
    saving?: boolean;
}

let stepCounter = 0;
const nextKey = () => `step_${++stepCounter}`;

const OperationBuilder: React.FC<OperationBuilderProps> = ({
    open, onClose, onSave, missions, devices, saving,
}) => {
    const [form] = Form.useForm();
    const [steps, setSteps] = useState<MissionStepDraft[]>([]);

    const addStep = () => {
        setSteps((prev) => [
            ...prev,
            {
                key: nextKey(),
                missionId: null,
                deviceId: null,
                executionOrder: prev.length + 1,
                triggerType: 'auto',
                dependsOnKeys: [],
            },
        ]);
    };

    const removeStep = (key: string) => {
        setSteps((prev) =>
            prev
                .filter((s) => s.key !== key)
                .map((s, i) => ({
                    ...s,
                    executionOrder: i + 1,
                    dependsOnKeys: s.dependsOnKeys.filter((k) => k !== key),
                }))
        );
    };

    const updateStep = (key: string, patch: Partial<MissionStepDraft>) => {
        setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            const invalidSteps = steps.filter((s) => !s.missionId);
            if (invalidSteps.length > 0) {
                message.warning('Please select a mission for every step');
                return;
            }
            if (steps.length === 0) {
                message.warning('Add at least one mission step');
                return;
            }

            // Build dependency mapping: key -> position index
            const keyToIndex = new Map<string, number>();
            steps.forEach((s, i) => keyToIndex.set(s.key, i));

            await onSave({
                name: values.name,
                description: values.description || undefined,
                scheduledStartAt: values.scheduledStartAt ? values.scheduledStartAt.toISOString() : null,
                missions: steps.map((s) => ({
                    missionId: s.missionId!,
                    deviceId: s.deviceId || undefined,
                    executionOrder: s.executionOrder,
                    triggerType: s.triggerType,
                    triggerCondition: s.triggerType,
                    // Dependencies reference execution order (1-based) for the backend
                    dependsOnOperationMissionIds: s.dependsOnKeys
                        .map((k) => {
                            const idx = keyToIndex.get(k);
                            return idx !== undefined ? idx + 1 : null;
                        })
                        .filter((v): v is number => v !== null),
                })),
            });

            // Reset
            form.resetFields();
            setSteps([]);
        } catch {
            // validation failed
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setSteps([]);
        onClose();
    };

    return (
        <Modal
            open={open}
            onCancel={handleCancel}
            title={
                <Space>
                    <ApartmentOutlined />
                    <span>Create operation</span>
                </Space>
            }
            width="min(900px, 96vw)"
            footer={
                <Space>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>
                        Create operation
                    </Button>
                </Space>
            }
            destroyOnClose
        >
            <Form form={form} layout="vertical" size="small">
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                    Mission steps define the field, route, device, and commands for this operation.
                </Text>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <Form.Item
                            name="name"
                            label="Operation name"
                            rules={[{ required: true, message: 'Required' }]}
                        >
                            <Input placeholder="e.g. Full pitch scan" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                        <Form.Item name="scheduledStartAt" label="Scheduled start (optional)">
                            <DatePicker showTime style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item name="description" label="Description">
                    <TextArea rows={2} placeholder="Describe the operation…" />
                </Form.Item>
            </Form>

            <Divider orientation="left" plain>
                <Space>
                    <ApartmentOutlined />
                    Mission steps
                    <Tag>{steps.length}</Tag>
                </Space>
            </Divider>

            {steps.length === 0 ? (
                <Empty
                    description="No mission steps added yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
            ) : (
                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    {steps.map((step, idx) => (
                        <Card
                            key={step.key}
                            size="small"
                            className="operation-step-card"
                            title={
                                <Space>
                                    <Tag color="blue">#{idx + 1}</Tag>
                                    <Text strong style={{ fontSize: 12 }}>
                                        {step.missionId
                                            ? missions.find((m) => m.id === step.missionId)?.name || `Mission #${step.missionId}`
                                            : 'Select mission…'}
                                    </Text>
                                </Space>
                            }
                            extra={
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeStep(step.key)}
                                />
                            }
                        >
                            <Row gutter={[8, 8]}>
                                <Col xs={24} md={8}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Mission</Text>
                                    <Select
                                        style={{ width: '100%' }}
                                        size="small"
                                        showSearch
                                        placeholder="Select mission"
                                        optionFilterProp="label"
                                        value={step.missionId}
                                        onChange={(val) => updateStep(step.key, { missionId: val })}
                                        options={missions.map((m) => ({
                                            value: m.id,
                                            label: `${m.name} (${m.status})`,
                                        }))}
                                    />
                                </Col>
                                <Col xs={24} md={8}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>
                                        <RobotOutlined /> Device
                                    </Text>
                                    <Select
                                        style={{ width: '100%' }}
                                        size="small"
                                        allowClear
                                        showSearch
                                        placeholder="Override device"
                                        optionFilterProp="label"
                                        value={step.deviceId}
                                        onChange={(val) => updateStep(step.key, { deviceId: val })}
                                        options={devices.map((d) => ({
                                            value: d.id,
                                            label: d.label,
                                        }))}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Trigger</Text>
                                    <Select
                                        style={{ width: '100%' }}
                                        size="small"
                                        value={step.triggerType}
                                        onChange={(val) => updateStep(step.key, { triggerType: val })}
                                        options={TRIGGER_TYPE_OPTIONS.map((t) => ({
                                            value: t.value,
                                            label: t.label,
                                        }))}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <Text type="secondary" style={{ fontSize: 11 }}>Depends on</Text>
                                    <Select
                                        mode="multiple"
                                        style={{ width: '100%' }}
                                        size="small"
                                        placeholder="None"
                                        value={step.dependsOnKeys}
                                        onChange={(val) => updateStep(step.key, { dependsOnKeys: val })}
                                        options={steps
                                            .filter((s) => s.key !== step.key)
                                            .map((s, i) => ({
                                                value: s.key,
                                                label: `#${steps.indexOf(s) + 1} ${
                                                    s.missionId
                                                        ? missions.find((m) => m.id === s.missionId)?.name || ''
                                                        : '(empty)'
                                                }`,
                                            }))}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    ))}
                </Space>
            )}

            <div style={{ marginTop: 12 }}>
                <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={addStep}
                    block
                >
                    Add mission step
                </Button>
            </div>
        </Modal>
    );
};

export default OperationBuilder;
